/**
 * Site pack (F6 process + F7 pack hall): everything a machine operator's, store keeper's or pack
 * operator's phone needs to work a day offline at a processing site or a packing hall — machines
 * with their labels, the step registry, settings rows (yield bands), the last run per machine,
 * lots and bags at the site, issue tasks, runs open here, the site's OUTPUT_BAG / QC_SEAL /
 * MACHINE rolls, sack types and scales; at a packing hall also the pack runs with their device
 * bundle, pack lots waiting at the hall and the buyers they can go to.
 *
 * Endpoints: GET /process-runs/site-pack?locationId= (F6.1-d) and, per started pack run,
 * GET /pack-runs/{id}/device-bundle (F7.2-c / F7.3-e), merged into `pack_runs`.
 *
 * Units follow the phone's rule: weights are integer GRAMS (`_g`), money integer PAISE
 * (`_paise`). Settings percentages (yield band, loss limit) come as the server's numeric percent
 * and are turned into basis points by the models. No farmer names, phones, GPS or prices: a lot
 * carries its code and the farmer's roster code. Unknown extra fields are kept (loose objects).
 *
 * Storage: one row in `config` (id `site_pack`), sealed with the roster vault key when there is
 * one — same approach as the store cache.
 */
import { z } from 'zod'
import { getDb, type SupplyChainDB } from './db'
import { getVaultKey, teamMemberSchema } from './roster'
import {
  bagFromServer,
  lotsFromServerBags,
  stepFromServer,
  yieldBandFromServer,
} from './sitePackServer'
import { maybeSeal, maybeUnseal } from './vault'

const grams = z.number().int()
const holdEnum = z.enum(['NONE', 'QUARANTINE', 'HELD', 'RECALLED'])
const streamEnum = z.enum(['MAIN', 'CONV'])

export const RUN_STATUSES = [
  'OPEN',
  'RUNNING',
  'CLOSED',
  'BALANCED',
  'EXCEPTION',
  'CANCELLED',
] as const
export type RunStatus = (typeof RUN_STATUSES)[number]

/** One output a step makes (step registry, F6.1-b): a grade, a by-product, waste. */
export const stepOutputSchema = z.looseObject({
  key: z.string(),
  hi: z.string(),
  en: z.string(),
  /** BATCH (product grade), BYPRODUCT or REJECT (waste and sweepings). */
  unit_type: z.enum(['BATCH', 'BYPRODUCT', 'REJECT']),
  /** RUN = follows the run's stream (brand product); CONV = always ordinary. */
  stream: z.enum(['RUN', 'CONV']).default('RUN'),
  grade: z.string().nullish(),
  /** Goes on to packing (QC sample asked for on the last step before packing). */
  to_packing: z.boolean().default(false),
  /** Counted as product in the yield. */
  product: z.boolean().default(false),
})
export type StepOutput = z.infer<typeof stepOutputSchema>

export const photoPointSchema = z.looseObject({ key: z.string(), hi: z.string(), en: z.string() })
export type PhotoPoint = z.infer<typeof photoPointSchema>

export const settingsFieldSchema = z.looseObject({
  key: z.string(),
  hi: z.string(),
  en: z.string(),
  unit: z.string().nullish(),
})
export type SettingsField = z.infer<typeof settingsFieldSchema>

export const stepSchema = z.looseObject({
  step_code: z.string(),
  hi: z.string(),
  en: z.string(),
  link_type: z.enum(['TRANSFORM', 'SPLIT', 'REPACK']).default('TRANSFORM'),
  /** One loss limit per step (R28), in percent of kilos in. */
  loss_limit_pct: z.number().nonnegative(),
  outputs: z.array(stepOutputSchema).default([]),
  photo_points: z.array(photoPointSchema).default([]),
  settings_fields: z.array(settingsFieldSchema).default([]),
  /** Moisture is needed once per lot at the issue (DRY). */
  needs_moisture: z.boolean().default(false),
})
export type Step = z.infer<typeof stepSchema>

/** Crop route: the order of steps; the last one is the last before packing. */
export const routeSchema = z.looseObject({ crop_id: z.string(), steps: z.array(z.string()) })

export const machineSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: z.string().nullish(),
  location_id: z.string().nullish(),
  step_codes: z.array(z.string()).default([]),
  shared_use: z.boolean().default(true),
  /** The bound MACHINE label; null = no label yet (offered on the Machine label screen). */
  label_payload: z.string().nullish(),
})
export type Machine = z.infer<typeof machineSchema>

/** A settings row (yield_bands): the newest version per machine, crop (and variety) and step. */
export const yieldBandSchema = z.looseObject({
  id: z.string(),
  crop_id: z.string(),
  crop_variety_id: z.string().nullish(),
  step_code: z.string(),
  /** Null = crop-wide: used by any machine that has no row of its own. */
  equipment_id: z.string().nullish(),
  version: z.number().int(),
  effective_from: z.string(),
  min_pct: z.number(),
  max_pct: z.number(),
  min_flush_g: grams.nullish(),
  min_input_g: grams.nullish(),
})
export type YieldBand = z.infer<typeof yieldBandSchema>

/** What ran last on a machine (open shows it; flush and allergen-clean decisions use it). */
export const lastRunSchema = z.looseObject({
  equipment_id: z.string(),
  run_id: z.string(),
  code: z.string(),
  crop_id: z.string().nullish(),
  crop_name: z.string().nullish(),
  stream: streamEnum.nullish(),
  farmer_id: z.string().nullish(),
  farmer_code: z.string().nullish(),
  season_code: z.string().nullish(),
  status: z.string(),
  at: z.string().nullish(),
})
export type LastRun = z.infer<typeof lastRunSchema>

/** A lot or batch at the site (units). */
export const packLotUnitSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  /** LOT (farmer lot), ORPHAN, BATCH, BYPRODUCT, REJECT, PACK. */
  unit_type: z.string().default('LOT'),
  crop_id: z.string(),
  crop_name: z.string().nullish(),
  crop_variety_id: z.string().nullish(),
  farmer_id: z.string().nullish(),
  farmer_code: z.string().nullish(),
  season_code: z.string().nullish(),
  stream: streamEnum,
  hold: holdEnum.default('NONE'),
  hold_reason: z.string().nullish(),
  status: z.string().default('STORED'),
  /** An open problem on the lot (issue refuses it, R02). */
  open_exception: z.boolean().default(false),
  /** Gate net of a farmer lot / frozen net of a batch. */
  net_g: grams.nullish(),
  grade: z.string().nullish(),
  /** For a batch: the run that made it and the output key. */
  run_id: z.string().nullish(),
  stream_key: z.string().nullish(),
  /** A held batch the quality person wants a confirming sample of (F7-B1.b). */
  confirm_sample_requested: z.boolean().default(false),
})
export type SiteUnit = z.infer<typeof packLotUnitSchema>

export const siteBagSchema = z.looseObject({
  container_id: z.string(),
  qr_payload: z.string(),
  tag_kind: z.enum(['BAG', 'OUTPUT_BAG']).default('BAG'),
  unit_id: z.string(),
  /** SEALED | STORED | ISSUED | CONSUMED | WEIGHED | VOID | MISSING | IN_TRANSIT */
  status: z.string(),
  hold: holdEnum.default('NONE'),
  stream: streamEnum,
  location_id: z.string().nullish(),
  net_g_gate: grams.nullish(),
  net_g_current: grams.nullish(),
  tare_g: grams.default(0),
  sack_type_id: z.string().nullish(),
})
export type SiteBag = z.infer<typeof siteBagSchema>

/** A kind ISSUE shipment set in the console: these lots' bags to that run (F6.10). */
export const issueTaskSchema = z.looseObject({
  shipment_id: z.string(),
  code: z.string(),
  run_id: z.string(),
  run_code: z.string(),
  step_code: z.string().nullish(),
  status: z.string().default('LOADING'),
  unit_ids: z.array(z.string()).default([]),
  /** Bags of the task (all live bags of the lots at the site). */
  container_ids: z.array(z.string()).default([]),
  /** Bags already issued (on the server). */
  issued: z
    .array(z.looseObject({ container_id: z.string(), net_g: grams.nullish() }))
    .default([]),
  /** Units whose moisture was already typed at this issue. */
  moisture_unit_ids: z.array(z.string()).default([]),
})
export type IssueTask = z.infer<typeof issueTaskSchema>

export const runBalanceSchema = z.looseObject({
  input_g: grams,
  flush_g: grams.default(0),
  product_g: grams.default(0),
  byproduct_g: grams.default(0),
  reject_g: grams.default(0),
  output_g: grams.nullish(),
  residual_g: grams.nullish(),
  residual_pct: z.number().nullish(),
  yield_pct: z.number().nullish(),
  min_pct: z.number().nullish(),
  max_pct: z.number().nullish(),
  loss_limit_pct: z.number().nullish(),
  result: z.enum(['BALANCED', 'EXCEPTION']).nullish(),
  kinds: z.array(z.string()).default([]),
})
export type RunBalance = z.infer<typeof runBalanceSchema>

export const runInputSchema = z.looseObject({
  id: z.string(),
  container_id: z.string(),
  unit_id: z.string(),
  qr_payload: z.string(),
  weight_g: grams,
  context: z.enum(['ISSUE', 'WEIGH_IN']).default('ISSUE'),
  scanned_at: z.string().nullish(),
})

export const runOutputSchema = z.looseObject({
  container_id: z.string(),
  unit_id: z.string(),
  unit_code: z.string().nullish(),
  stream_key: z.string(),
  qr_payload: z.string(),
  sack_type_id: z.string().nullish(),
  tare_g: grams.default(0),
  read_g: grams,
  net_g: grams,
  stream: streamEnum,
  status: z.string().default('WEIGHED'),
  /** The batch already has a sample on the server (any phone). Older servers leave it out. */
  sampled: z.boolean().nullish(),
  /** The batch's own status on the server, e.g. CONSUMED once packed. Older servers leave it out. */
  unit_status: z.string().nullish(),
})

/** A run known to the server at this site (open, or closed recently). */
export const siteRunSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  equipment_id: z.string(),
  step_code: z.string(),
  operator_id: z.string().nullish(),
  cleanout_by_id: z.string().nullish(),
  status: z.enum(RUN_STATUSES),
  stream: streamEnum,
  crop_id: z.string(),
  crop_name: z.string().nullish(),
  origin_unit_id: z.string().nullish(),
  origin_unit_ids: z.array(z.string()).default([]),
  farmer_id: z.string().nullish(),
  farmer_code: z.string().nullish(),
  season_code: z.string().nullish(),
  yield_band_id: z.string().nullish(),
  flush_required: z.boolean().default(false),
  flush_min_g: grams.nullish(),
  // null on a pack run (the server sets it only for processing steps): not required.
  allergen_clean_required: z.boolean().nullish().transform((v) => v ?? false),
  previous_run: lastRunSchema.partial().nullish(),
  opened_at: z.string().nullish(),
  cleanout_confirmed_at: z.string().nullish(),
  started_at: z.string().nullish(),
  ended_at: z.string().nullish(),
  inputs: z.array(runInputSchema).default([]),
  outputs: z.array(runOutputSchema).default([]),
  zero_streams: z.array(z.string()).default([]),
  mixing_reason: z.string().nullish(),
  balance: runBalanceSchema.nullish(),
  /** Open problems on the run (balance, late entry, mixing …). */
  open_exceptions: z
    .array(z.looseObject({ id: z.string(), type: z.string(), kinds: z.array(z.string()).default([]) }))
    .default([]),
  /** QC samples sealed on this run's batches. */
  samples: z
    .array(
      z.looseObject({
        sample_id: z.string(),
        unit_id: z.string(),
        seal_payload: z.string(),
        status: z.string().default('SEALED'),
        purpose: z.string().nullish(),
      }),
    )
    .default([]),
  /** DELABEL runs: pouches set at the console. */
  params: z.record(z.string(), z.unknown()).default({}),
})
export type SiteRun = z.infer<typeof siteRunSchema>

export const siteRollSchema = z.looseObject({
  batch_id: z.string(),
  /** OUTPUT_BAG, QC_SEAL, MACHINE (and BAG / SEAL rolls seen here, to name a wrong kind). */
  kind: z.string(),
  payload_prefix: z.string(),
  start_sequence: z.number().int(),
  end_sequence: z.number().int(),
  issued_to_id: z.string().nullish(),
  used: z.array(z.string()).default([]),
})
export type SiteRoll = z.infer<typeof siteRollSchema>

export const siteSackTypeSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  tare_g: grams,
  /** The usual output sack, preselected. */
  default_output: z.boolean().default(false),
})
export type SiteSackType = z.infer<typeof siteSackTypeSchema>

export const siteScaleSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  category: z.string().default('SCALE'),
  calibration_due_at: z.string().nullish(),
  stamp_due_at: z.string().nullish(),
  check_tolerance_g: grams.default(2),
})
export type SiteScale = z.infer<typeof siteScaleSchema>

// ---------- packing hall ----------

export const skuSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  name_hi: z.string().nullish(),
  net_g: grams,
  tolerance_g: grams,
  pouches_per_carton: z.number().int().positive(),
  shelf_life_days: z.number().int().nullish(),
  gtin: z.string().nullish(),
})
export type Sku = z.infer<typeof skuSchema>

export const labelVersionSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  status: z.string().default('APPROVED'),
  mrp_paise: z.number().int().nullish(),
  allergen_statement: z.string().nullish(),
  claim_text: z.string().nullish(),
  fssai_licence_no: z.string().nullish(),
})
export type LabelVersion = z.infer<typeof labelVersionSchema>

export const packLotSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  run_id: z.string().nullish(),
  sku_id: z.string().nullish(),
  sku_name: z.string().nullish(),
  net_g: grams.nullish(),
  pack_date: z.string(),
  best_before: z.string(),
  qr_url: z.string(),
  /** PACKING | CLOSED | RELEASED | WITHDRAWN | DESTROYED */
  status: z.string(),
  hold: z.enum(['NONE', 'HELD', 'RECALLED']).default('NONE'),
  hold_reason: z.string().nullish(),
  pouch_count: z.number().int().nullish(),
  pouches_dispatched: z.number().int().default(0),
  released_at: z.string().nullish(),
  label_version_code: z.string().nullish(),
})
export type PackLot = z.infer<typeof packLotSchema>

export const packRunSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  /** OPEN (planned or started) | RUNNING | CLOSED | BALANCED | EXCEPTION | CANCELLED */
  status: z.enum(RUN_STATUSES),
  equipment_id: z.string().nullish(),
  line_name: z.string().nullish(),
  operator_id: z.string().nullish(),
  batch_unit_id: z.string(),
  batch_code: z.string(),
  crop_id: z.string(),
  crop_name: z.string().nullish(),
  farmer_code: z.string().nullish(),
  batch_hold: holdEnum.default('NONE'),
  sku: skuSchema,
  planned_g: grams,
  planned_pouches: z.number().int(),
  planned_date: z.string(),
  /** Approved label versions for this SKU at this hall (start picks one). */
  label_versions: z.array(labelVersionSchema).default([]),
  label_version_id: z.string().nullish(),
  previous_crop_id: z.string().nullish(),
  previous_crop_name: z.string().nullish(),
  // null on a pack run (the server sets it only for processing steps): not required.
  allergen_clean_required: z.boolean().nullish().transform((v) => v ?? false),
  premises_licence_no: z.string().nullish(),
  premises_licence_valid_to: z.string().nullish(),
  started_at: z.string().nullish(),
  pack_lot: packLotSchema.nullish(),
  /** Batch sacks issued to this run (the store's issue step). */
  issued_container_ids: z.array(z.string()).default([]),
  /** Pouch numbers at which a spot weight is asked (device bundle). */
  spot_every: z.number().int().positive().default(50),
  /** When the device bundle was last fetched. */
  bundle_at: z.string().nullish(),
  /** What the server already has (resumed on another phone or after a reinstall). */
  inputs: z.array(runInputSchema).default([]),
  outputs: z.array(runOutputSchema).default([]),
  balance: z
    .looseObject({
      input_g: grams,
      packed_g: grams,
      leftover_g: grams,
      spillage_g: grams,
      unaccounted_pct: z.number(),
      in_limit: z.boolean(),
    })
    .nullish(),
})
export type PackRun = z.infer<typeof packRunSchema>

export const buyerSchema = z.looseObject({ id: z.string(), name: z.string(), code: z.string().nullish() })

export const sitePackSettingsSchema = z.looseObject({
  /** A reading outside this range asks for a second entry (measurements rule). */
  weight_min_g: grams.default(5_000),
  weight_max_g: grams.default(60_000),
  /** Pack weigh-in: typed net further than this from the machine net asks for a second entry. */
  pack_sack_margin_g: grams.default(500),
  /** Stop the line and call the supervisor when this share of spot weights is low (%). */
  low_pouch_stop_pct: z.number().default(10),
  /** Contact on warning pages and the phone's hold banners. */
  quality_contact: z.string().nullish(),
})
export type SitePackSettings = z.infer<typeof sitePackSettingsSchema>

const sitePackObject = z.looseObject({
  site_id: z.string(),
  site_code: z.string().nullish(),
  site_name: z.string().nullish(),
  /** PROCESSING | PACKING */
  site_kind: z.string().nullish(),
  /** The machine area every issued bag moves to (F6.10). */
  machine_area_location_id: z.string().nullish(),
  generated_at: z.string(),
  steps: z.array(stepSchema).default([]),
  routes: z.array(routeSchema).default([]),
  machines: z.array(machineSchema).default([]),
  yield_bands: z.array(yieldBandSchema).default([]),
  last_runs: z.array(lastRunSchema).default([]),
  lots: z.array(packLotUnitSchema).default([]),
  bags: z.array(siteBagSchema).default([]),
  issue_tasks: z.array(issueTaskSchema).default([]),
  runs: z.array(siteRunSchema).default([]),
  rolls: z.array(siteRollSchema).default([]),
  dead_tags: z
    .array(z.looseObject({ qr_payload: z.string(), status: z.enum(['VOID', 'RETIRED']) }))
    .default([]),
  sack_types: z.array(siteSackTypeSchema).default([]),
  scales: z.array(siteScaleSchema).default([]),
  pack_runs: z.array(packRunSchema).default([]),
  pack_lots: z.array(packLotSchema).default([]),
  buyers: z.array(buyerSchema).default([]),
  settings: sitePackSettingsSchema.default(sitePackSettingsSchema.parse({})),
  team: z.array(teamMemberSchema).default([]),
})
/**
 * The server names a few lists differently (`open_runs`, `settings` for the yield-band rows) and
 * sends steps and settings rows in its own shape (sitePackServer.ts); read either so the phone
 * works with both.
 */
export const sitePackSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== 'object') return raw
  const r = raw as Record<string, unknown>
  const bands = r.yield_bands ?? (Array.isArray(r.settings) ? r.settings : undefined)
  return {
    ...r,
    runs: r.runs ?? r.open_runs,
    steps: Array.isArray(r.steps) ? r.steps.map(stepFromServer) : r.steps,
    bags: Array.isArray(r.bags) ? r.bags.map(bagFromServer) : r.bags,
    lots: r.lots ?? (Array.isArray(r.bags) ? lotsFromServerBags(r.bags) : undefined),
    yield_bands: Array.isArray(bands) ? bands.map(yieldBandFromServer) : bands,
    settings: Array.isArray(r.settings) ? undefined : r.settings,
  }
}, sitePackObject)
export type SitePack = z.infer<typeof sitePackObject>

export const SITE_PACK_ROW_ID = 'site_pack'

export interface StoredSitePack {
  pack: SitePack
  downloaded_at: number
}

/**
 * Save a downloaded site pack. The team and places for PIN unlock come from the site's store
 * cache (downloaded first, same site), so they are not touched here.
 */
export async function saveSitePack(
  pack: SitePack,
  db: SupplyChainDB = getDb(),
  now: number = Date.now(),
): Promise<void> {
  const key = await getVaultKey(db)
  await db.config.put({
    id: SITE_PACK_ROW_ID,
    data: await maybeSeal<StoredSitePack>(key, { pack, downloaded_at: now }),
  })
}

export async function readSitePack(
  db: SupplyChainDB = getDb(),
): Promise<StoredSitePack | undefined> {
  const row = await db.config.get(SITE_PACK_ROW_ID)
  if (!row) return undefined
  return maybeUnseal<StoredSitePack>(await getVaultKey(db), row.data as StoredSitePack)
}

/** Change the stored pack in place (a pack-run bundle, or a demo-mode answer), keeping its age. */
export async function patchSitePack(
  fn: (pack: SitePack) => SitePack,
  db: SupplyChainDB = getDb(),
): Promise<void> {
  const cur = await readSitePack(db)
  if (!cur) return
  const key = await getVaultKey(db)
  await db.config.put({
    id: SITE_PACK_ROW_ID,
    data: await maybeSeal<StoredSitePack>(key, { pack: fn(cur.pack), downloaded_at: cur.downloaded_at }),
  })
}

export async function wipeSitePack(db: SupplyChainDB = getDb()): Promise<void> {
  await db.config.delete(SITE_PACK_ROW_ID)
}
